import {
  EQUIPMENT_CATALOG,
  INITIAL_INVENTORY,
  PICKUP_LOCATIONS,
  QR_VERIFICATION_STATUSES,
  RESERVATION_STATUSES,
} from '../data/equipmentLendingLibrary';

const INVENTORY_STORAGE_KEY = 'toxisee.equipmentLibrary.inventory.v1';
const RESERVATIONS_STORAGE_KEY = 'toxisee.equipmentLibrary.reservations.v1';

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function getStorage() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  return window.localStorage;
}

function readStoredJson(storageKey, fallbackValue) {
  const storage = getStorage();
  if (!storage) {
    return clone(fallbackValue);
  }

  try {
    const raw = storage.getItem(storageKey);
    if (!raw) {
      return clone(fallbackValue);
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : clone(fallbackValue);
  } catch {
    return clone(fallbackValue);
  }
}

function writeStoredJson(storageKey, value) {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  storage.setItem(storageKey, JSON.stringify(value));
}

function ensureInventory() {
  const inventory = readStoredJson(INVENTORY_STORAGE_KEY, INITIAL_INVENTORY);
  writeStoredJson(INVENTORY_STORAGE_KEY, inventory);
  return inventory;
}

function ensureReservations() {
  const reservations = readStoredJson(RESERVATIONS_STORAGE_KEY, []);
  writeStoredJson(RESERVATIONS_STORAGE_KEY, reservations);
  return reservations;
}

function randomToken(length = 4) {
  return Math.random().toString(36).slice(2, 2 + length).toUpperCase();
}

function formatDateStamp(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

function buildReservationId() {
  return `ELL-${formatDateStamp()}-${randomToken(5)}`;
}

function buildQrValue(reservationId) {
  return `toxisee://equipment-lending-library/reservations/${reservationId}`;
}

function normalizeReservationLookup(value) {
  const input = String(value || '').trim();
  if (!input) {
    return '';
  }

  const prefix = 'toxisee://equipment-lending-library/reservations/';
  if (input.startsWith(prefix)) {
    return input.slice(prefix.length).trim();
  }

  return input;
}

function createReservationRecord(payload) {
  const reservationId = buildReservationId();
  const now = new Date().toISOString();

  return {
    id: reservationId,
    equipmentId: payload.equipmentId,
    locationId: payload.locationId,
    borrowerName: payload.borrowerName,
    email: payload.email,
    phone: payload.phone,
    preferredPickupDate: payload.preferredPickupDate,
    pickupDeadline: payload.preferredPickupDate,
    qrCodeValue: buildQrValue(reservationId),
    status: RESERVATION_STATUSES.HELD,
    verificationStatus: QR_VERIFICATION_STATUSES.VALID,
    createdAt: now,
    updatedAt: now,
    pickedUpAt: null,
    returnedAt: null,
  };
}

/**
 * Reservation model:
 * {
 *   id: string,
 *   equipmentId: string,
 *   locationId: string,
 *   borrowerName: string,
 *   email: string,
 *   phone: string,
 *   preferredPickupDate: string,
 *   pickupDeadline: string,
 *   qrCodeValue: string,
 *   status: 'held' | 'picked_up' | 'returned',
 *   verificationStatus: 'valid' | 'picked_up' | 'returned' | 'invalid',
 *   createdAt: string,
 *   updatedAt: string,
 *   pickedUpAt: string | null,
 *   returnedAt: string | null,
 * }
 */
export function getEquipmentLibrarySnapshot() {
  return {
    equipment: clone(EQUIPMENT_CATALOG),
    locations: clone(PICKUP_LOCATIONS),
    inventory: ensureInventory(),
    reservations: ensureReservations(),
  };
}

export function reserveEquipment(payload) {
  const inventory = ensureInventory();
  const reservations = ensureReservations();

  const inventoryRecord = inventory.find(
    (item) => item.equipmentId === payload.equipmentId && item.locationId === payload.locationId,
  );

  if (!inventoryRecord || inventoryRecord.availableCount < 1) {
    throw new Error('That device is no longer available at the selected pickup site.');
  }

  inventoryRecord.availableCount -= 1;

  const reservation = createReservationRecord(payload);
  reservations.unshift(reservation);

  writeStoredJson(INVENTORY_STORAGE_KEY, inventory);
  writeStoredJson(RESERVATIONS_STORAGE_KEY, reservations);

  return clone(reservation);
}

export function findReservation(identifier) {
  const normalizedId = normalizeReservationLookup(identifier);
  if (!normalizedId) {
    return null;
  }

  const reservations = ensureReservations();
  const reservation = reservations.find(
    (item) => item.id === normalizedId || item.qrCodeValue === identifier,
  );

  return reservation ? clone(reservation) : null;
}

export function markReservationPickedUp(reservationId) {
  const inventory = ensureInventory();
  const reservations = ensureReservations();
  const reservation = reservations.find((item) => item.id === reservationId);

  if (!reservation) {
    throw new Error('Reservation not found.');
  }

  if (reservation.status === RESERVATION_STATUSES.PICKED_UP) {
    return clone(reservation);
  }

  if (reservation.status === RESERVATION_STATUSES.RETURNED) {
    throw new Error('Returned reservations cannot be checked out again.');
  }

  const inventoryRecord = inventory.find(
    (item) => item.equipmentId === reservation.equipmentId && item.locationId === reservation.locationId,
  );

  if (!inventoryRecord) {
    throw new Error('Inventory record not found for this reservation.');
  }

  reservation.status = RESERVATION_STATUSES.PICKED_UP;
  reservation.verificationStatus = QR_VERIFICATION_STATUSES.PICKED_UP;
  reservation.pickedUpAt = new Date().toISOString();
  reservation.updatedAt = reservation.pickedUpAt;
  inventoryRecord.onLoanCount += 1;

  writeStoredJson(INVENTORY_STORAGE_KEY, inventory);
  writeStoredJson(RESERVATIONS_STORAGE_KEY, reservations);

  return clone(reservation);
}

export function markReservationReturned(reservationId) {
  const inventory = ensureInventory();
  const reservations = ensureReservations();
  const reservation = reservations.find((item) => item.id === reservationId);

  if (!reservation) {
    throw new Error('Reservation not found.');
  }

  if (reservation.status === RESERVATION_STATUSES.RETURNED) {
    return clone(reservation);
  }

  if (reservation.status !== RESERVATION_STATUSES.PICKED_UP) {
    throw new Error('Only picked up reservations can be returned.');
  }

  const inventoryRecord = inventory.find(
    (item) => item.equipmentId === reservation.equipmentId && item.locationId === reservation.locationId,
  );

  if (!inventoryRecord) {
    throw new Error('Inventory record not found for this reservation.');
  }

  reservation.status = RESERVATION_STATUSES.RETURNED;
  reservation.verificationStatus = QR_VERIFICATION_STATUSES.RETURNED;
  reservation.returnedAt = new Date().toISOString();
  reservation.updatedAt = reservation.returnedAt;
  inventoryRecord.availableCount += 1;
  inventoryRecord.onLoanCount = Math.max(0, inventoryRecord.onLoanCount - 1);

  writeStoredJson(INVENTORY_STORAGE_KEY, inventory);
  writeStoredJson(RESERVATIONS_STORAGE_KEY, reservations);

  return clone(reservation);
}
